/**
 * ============================================================================
 * 🌐 HEAREN HTTP CLIENT - iOS WebView Compatible
 * ============================================================================
 * 
 * Ce client centralise tous les appels HTTP pour garantir :
 * - URL absolue forcée (pas de relative URL)
 * - Compatibilité iOS Capacitor WebView (évite InvalidStateError)
 * - Fallback automatique si env var manquante
 * - Gestion propre des annulations de requêtes
 * 
 * USAGE:
 *   import { apiClient, API_BASE_URL } from '../services/httpClient';
 *   const response = await apiClient.post('/api/auth/login', data);
 * ============================================================================
 */

import axios from 'axios';

// ============================================================================
// 🔒 FORCED BASE URL - Never empty, never relative
// ============================================================================

// iOS/Capacitor detection - must be checked BEFORE getBaseUrl()
const isCapacitorNative = () => {
  if (typeof window === 'undefined') return false;
  // Check for Capacitor native platform (not web)
  if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
    return true;
  }
  // Fallback: check if origin is capacitor:// or ionic://
  if (window.location && (
    window.location.origin.startsWith('capacitor://') ||
    window.location.origin.startsWith('ionic://') ||
    window.location.protocol === 'capacitor:' ||
    window.location.protocol === 'ionic:'
  )) {
    return true;
  }
  return false;
};

const getBaseUrl = () => {
  if (
    typeof window !== 'undefined' &&
    window.__HEAREN_BACKEND_URL__
  ) {
    return window.__HEAREN_BACKEND_URL__;
  }

  const envUrl = process.env.REACT_APP_BACKEND_URL;

  if (envUrl && envUrl.trim() !== '') {
    return envUrl.trim();
  }

  if (isCapacitorNative()) {
    console.log(
      '[httpClient] Capacitor native detected — using Render backend'
    );

    return 'https://hearen-backend.onrender.com';
  }

  return 'https://hearen-backend.onrender.com';
};

// Export the function for other services to use
export { getBaseUrl };

/**
 * V1.9: Build a properly encoded audio URL for R2 streaming
 * Centralizes URL encoding to prevent 404s on filenames with spaces
 * @param {string} category - Audio category (e.g., 'nature', 'pulse_flow', 'breathing')
 * @param {string} filename - Audio filename (e.g., 'Pulse Flow_001.m4a')
 * @param {boolean} includeBase - Whether to include the base URL (default: true)
 * @returns {string} Properly encoded URL
 */
export const buildAudioUrl = (category, filename, includeBase = true) => {
  // Ensure .m4a extension
  const m4aFilename = filename.toLowerCase().endsWith('.m4a') 
    ? filename 
    : filename.replace(/\.[^.]+$/, '.m4a');
  
  // URL-encode both category and filename to handle spaces and special characters
  const encodedCategory = encodeURIComponent(category.toLowerCase());
  const encodedFilename = encodeURIComponent(m4aFilename);
  
  const path = `/api/r2/stream/${encodedCategory}/${encodedFilename}`;
  
  if (includeBase) {
    const baseUrl = getBaseUrl();
    console.log(`[Audio] buildAudioUrl: ${baseUrl}${path}`);
    return `${baseUrl}${path}`;
  }
  
  return path;
};

/**
 * V1.9: Re-encode an existing audio URL path to ensure proper encoding
 * Use when receiving URLs from backend that may have unencoded characters
 * @param {string} urlPath - URL path like '/api/r2/stream/pulse_flow/Pulse Flow_001.m4a'
 * @returns {string} Properly encoded URL path
 */
export const reencodeAudioPath = (urlPath) => {
  if (!urlPath || !urlPath.includes('/api/r2/stream/')) {
    return urlPath;
  }
  
  // Parse the path: /api/r2/stream/{category}/{filename}
  const parts = urlPath.split('/');
  if (parts.length >= 6) {
    // parts = ['', 'api', 'r2', 'stream', 'category', 'filename']
    const category = parts[4];
    const filename = parts.slice(5).join('/'); // In case filename has /
    
    // Decode first (in case already partially encoded), then re-encode
    const decodedFilename = decodeURIComponent(filename);
    const encodedFilename = encodeURIComponent(decodedFilename);
    
    return `/api/r2/stream/${encodeURIComponent(category)}/${encodedFilename}`;
  }
  
  return urlPath;
};

// Computed once at startup
export const API_BASE_URL = getBaseUrl();

// Alias for services that use BACKEND_URL
export const BACKEND_URL = API_BASE_URL;

// Log at startup for debugging
console.log('[httpClient] API_BASE_URL:', API_BASE_URL);
console.log('[httpClient] isCapacitorNative:', isCapacitorNative());

// ============================================================================
// 🔧 iOS WebView Detection
// ============================================================================
const isIOSWebView = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isCapacitor = window.Capacitor !== undefined;
  return isIOS || isCapacitor;
};

const IS_IOS = isIOSWebView();
console.log('[httpClient] iOS WebView detected:', IS_IOS);

// ============================================================================
// 🚀 AXIOS INSTANCE with iOS-safe configuration
// ============================================================================
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: IS_IOS ? 45000 : 30000, // Longer timeout for iOS
  headers: {
    'Content-Type': 'application/json',
  },
  // iOS: Disable default transformRequest that can cause issues
  validateStatus: (status) => status >= 200 && status < 500,
});

// ============================================================================
// 📡 REQUEST INTERCEPTOR - iOS Safe with URL validation + Auth Debug
// ============================================================================
let requestCounter = 0;

apiClient.interceptors.request.use(
  (config) => {
    // Generate request ID for tracking
    const requestId = ++requestCounter;
    config._requestId = requestId;
    
    // Validate URL - guard against empty/undefined URLs that crash iOS WebView
    if (!config.url || config.url.trim() === '') {
      console.error(`[httpClient] #${requestId} BLOCKED: Empty URL`);
      return Promise.reject(new Error('Empty URL not allowed'));
    }
    
    // Full URL for logging
    const fullUrl = (config.baseURL || '') + config.url;
    
    // Guard against invalid URLs
    if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
      console.error(`[httpClient] #${requestId} BLOCKED: Invalid URL protocol: ${fullUrl}`);
      return Promise.reject(new Error(`Invalid URL: ${fullUrl}`));
    }
    
    // [HTTP][REQ] — Structured auth debug log
    const hasAuth = !!config.headers?.Authorization || !!apiClient.defaults.headers.common?.['Authorization'];
    const isNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() || false;
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    console.log(
      `[HTTP][REQ] ${config.method?.toUpperCase()} ${fullUrl} auth=${hasAuth ? 'yes' : 'no'} withCredentials=${!!config.withCredentials} online=${isOnline} native=${isNative}`
    );
    
    // iOS: Remove AbortController signal if present (causes InvalidStateError)
    if (IS_IOS && config.signal) {
      console.warn(`[httpClient] #${requestId} iOS: Removing AbortController signal`);
      delete config.signal;
    }
    
    // iOS: Ensure we don't have conflicting timeout mechanisms
    if (IS_IOS) {
      config.timeout = config.timeout || 45000;
    }
    
    return config;
  },
  (error) => {
    console.error('[HTTP][ERR] Request setup error:', error.message);
    return Promise.reject(error);
  }
);

// ============================================================================
// 📥 RESPONSE INTERCEPTOR - Error handling + Structured Debug Logs
// ============================================================================
apiClient.interceptors.response.use(
  (response) => {
    // [HTTP][RES] — Success log (only for auth-related endpoints to keep noise low)
    const url = response.config?.url || '';
    if (url.includes('/auth/')) {
      console.log(`[HTTP][RES] ${response.config?.method?.toUpperCase()} ${url} status=${response.status}`);
    }

    // Handle non-2xx responses that passed validateStatus
    if (response.status >= 400) {
      const error = new Error(response.data?.detail || `HTTP ${response.status}`);
      error.response = response;
      error.status = response.status;
      // [HTTP][ERR] — structured error for 4xx caught by validateStatus
      console.error(
        `[HTTP][ERR] ${response.config?.method?.toUpperCase()} ${url}\n` +
        `  message=${error.message}\n` +
        `  code=ERR_BAD_REQUEST\n` +
        `  hasResponse=yes\n` +
        `  status=${response.status}\n` +
        `  data=${JSON.stringify(response.data)}`
      );
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    // [HTTP][ERR] — Structured error log for all failure types
    const url = error.config?.url || error.request?.responseURL || '(unknown)';
    const method = error.config?.method?.toUpperCase() || '?';
    console.error(
      `[HTTP][ERR] ${method} ${url}\n` +
      `  message=${error.message}\n` +
      `  code=${error.code || 'none'}\n` +
      `  hasResponse=${error.response ? 'yes' : 'no'}\n` +
      `  status=${error.response?.status ?? 'undefined'}\n` +
      `  data=${error.response?.data ? JSON.stringify(error.response.data) : 'undefined'}`
    );

    // iOS: Handle InvalidStateError gracefully
    if (error.name === 'InvalidStateError' || error.message?.includes('invalid state')) {
      const customError = new Error('Request was cancelled');
      customError.code = 'REQUEST_CANCELLED';
      customError.isIOSAbort = true;
      return Promise.reject(customError);
    }
    
    return Promise.reject(error);
  }
);

// ============================================================================
// 🔐 AUTH HELPERS
// ============================================================================
export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

export const getAuthToken = () => {
  return apiClient.defaults.headers.common['Authorization']?.replace('Bearer ', '');
};

// ============================================================================
// 🛡️ iOS-SAFE REQUEST WRAPPER
// ============================================================================
// Use this for critical requests that must not fail on iOS
export const safeRequest = async (method, url, data = null, options = {}) => {
  try {
    const config = {
      method,
      url,
      ...options,
    };
    
    if (data && (method === 'post' || method === 'put' || method === 'patch')) {
      config.data = data;
    }
    
    // iOS: Remove any abort signals
    if (IS_IOS) {
      delete config.signal;
      delete config.cancelToken;
    }
    
    const response = await apiClient.request(config);
    return response;
  } catch (error) {
    // iOS: Don't throw on cancelled requests
    if (error.isIOSAbort || error.code === 'REQUEST_CANCELLED') {
      console.warn('[httpClient] iOS request cancelled, returning null');
      return null;
    }
    throw error;
  }
};

export default apiClient;
